import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ADD_OBSERVATION_COLUMNS_DATA,
  INVESTIGATION_OBSERVATION_COLUMNS_DATA,
} from "./config";
import { useAlert } from "context/alert";
import { useDispatch, useSelector } from "react-redux";
import TitleComponent from "./TitleComponet";
import InvestigationResults from "./InvestigationResults";
import Table from "components/Common/Table/index";
import {
  createPatientLabResultEntry,
  fetchInvestigationObservationDropDown,
  fetchObservationResultEntryData,
  fetchOnlyOrganismData,
  fetchReportRoleById,
} from "services/resultEntryReportMaster";
import InternalDoctorDropdown from "components/Common/Dropdowns/InternalDoctorDropdown";
import Loader from "components/Common/loader";
import { STALE_TIME } from "utils/constants";
import CommentNameDropdown from "components/Common/Dropdowns/CommentNameDropdown";
import TextEditor from "components/Common/TextEditor/editor";
import Swal from "sweetalert2";
import {
  resetResultEntryObservations,
  setResultEntryObservations,
  updateResultEntryObservation,
} from "../../../redux/action/result_entry_observation_action";
import TemplateDropDown from "components/Common/Dropdowns/TemplateDropDown";
import { fetchInvestigationObservations } from "services/investigationManager";
import zoneLocalStorageId from "utils/zone";
import companyLocalStorageId from "utils/companyID";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

function constructFormulasForFilledValues(filledValues, observations) {
  const result = observations.filter((observation) => {
    const array = [];
    observation.fields.forEach((field) => {
      const observation = filledValues.find(
        (item) => item.id === parseInt(field.replace("#", "")),
      );
      if (observation) array.push(observation.value);
    });
    if (array.length === observation.fields.length) return true;
    return false;
  });
  return result.map((observation) => {
    let formula = observation.formula;

    let observationValue = null;
    const filledValue = filledValues.find(
      (v) => v.id === observation.id && v?.isDirectChange,
    );
    if (filledValue) observationValue = filledValue.value;

    observation.fields.forEach((field) => {
      const id = parseInt(field.replace("#", "").replace("@", ""));
      const found = filledValues.find((item) => item.id === id);
      if (found) {
        formula = formula.replace(new RegExp(`#${id}@`, "g"), found.value);
      }
    });

    if (!observationValue) {
      try {
        if (formula.includes("Math.pow(") && formula.includes("*")) {
          const mathPowRegex = /Math\.pow\(([^)]+)\)/g;
          formula = formula.replace(mathPowRegex, (match, args) => {
            if (args.includes("*")) {
              const parts = args.split(",");
              if (parts.length === 2) {
                const base = eval(parts[0].trim());
                const exponent = eval(parts[1].trim());
                return Math.pow(base, exponent);
              } else {
                const evaluatedArgs = eval(args);
                return Math.pow(evaluatedArgs, 2);
              }
            }
            return match;
          });
        }

        observationValue = eval(formula);
      } catch (error) {
        console.error(
          `Error evaluating formula: ${observation.formula}`,
          error,
        );
      }
    }

    return { ...observation, observationValue };
  });
}

function constructFormulaFieldsFromObservations(observation) {
  const departmentKeys = Object.keys(
    observation.department_investigation || {},
  );

  const result = departmentKeys.flatMap((department) => {
    const investigationKeys = Object.keys(
      observation.department_investigation[department] || {},
    );
    return investigationKeys.flatMap((investigation) => {
      return observation.department_investigation[department][
        investigation
      ]?.observations
        .map((item) => ({
          id: item.observation_id,
          department,
          investigation,
          name: item.observation_name.trim(),
          formula: item.observation_formula,
          fields:
            item.observation_formula
              ?.match(/#\d+@/g)
              ?.map((field) => field.trim()) || [],
        }))
        .filter((item) => item.formula);
    });
  });

  return result;
}

function endsWithDotAndZeros(str) {
  const dotIndex = str.lastIndexOf(".");
  if (dotIndex === -1) return false;

  const afterDot = str.slice(dotIndex + 1);
  return afterDot.length > 0 && /^0+$/.test(afterDot);
}

const AddObservationValue = ({
  onClose,
  rowData,
  handleDocuments,
  documentInfo,
  refetchBussinessReport,
  isOpenObservationValue,
  onObservationValueClose,
}) => {
  const dispatch = useDispatch();
  const observationDataRedux = useSelector(
    (state) => state.resultEntryObservationReducer,
  );

  const { showAlert } = useAlert();

  const [showEditor, setShowEditor] = useState(false);

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [updatedRows, setUpdatedRows] = useState({});
  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const [filterValue, setFilterValue] = useState();
  const [doctorId, setdoctorId] = useState();
  // const [comment, setComment] = useState("");
  const [investigationId, setInvestigationId] = useState(
    rowData?.investigation_data?.investigation_id,
  );
  // const [commentName, setCommentName] = useState();
  const [commentsMap, setCommentsMap] = useState({});
  const [commentNamesMap, setCommentNamesMap] = useState({});
  const [templateDescription, setTemplateDescription] = useState();
  const [status, setStatus] = useState(null);
  const [selectedInvestigationIds, setSelectedInvestigationIds] = useState([]);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState();
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const [selectedInvestigations, setSelectedInvestigations] = useState({});
  const selectedInvestigationsRef = useRef({});
  const [investigationDataMap, setInvestigationDataMap] = useState({});

  // Sync selectedInvestigationsRef with selectedInvestigations state
  useEffect(() => {
    selectedInvestigationsRef.current = selectedInvestigations;
  }, [selectedInvestigations]);
  const [constructedObservations, setConstructedObservations] = useState([]);
  const [updatedFormulas, setUpdatedFormulas] = useState([]);
  const [filledObservations, setFilledObservations] = useState([]);
  const [template, setTemplate] = useState({});
  const [checkedInvestigations, setCheckedInvestigations] = useState({});

  const [observationData, setObservationData] = useState([]);
  const observationDataRef = useRef([]);

  const [isOrganismData, setIsOrganismData] = useState(false);
  const [organismObservations, setOrganismObservations] = useState([]);

  const [removedInvestigationIds, setRemovedInvestigationIds] = useState([]);

  const defaultCheckedInvestigations = useMemo(() => {
    const defaultChecked = {};

    if (observationDataRedux && Object.keys(observationDataRedux).length > 0) {
      Object.entries(observationDataRedux).forEach(
        ([department, investigations]) => {
          Object.entries(investigations).forEach(
            ([investigation_name, observations]) => {
              const investigationKey = `${department}-${investigation_name}`;
              defaultChecked[investigationKey] = true;
            },
          );
        },
      );
    }

    return defaultChecked;
  }, [observationDataRedux]);

  useEffect(() => {
    if (
      Object.keys(defaultCheckedInvestigations).length > 0 &&
      Object.keys(checkedInvestigations).length === 0
    ) {
      setCheckedInvestigations(defaultCheckedInvestigations);
    }
  }, [defaultCheckedInvestigations]);

  const handleCheckboxChange = (key) => {
    setCheckedInvestigations((prev) => ({
      ...prev,
      [key]: !Boolean(prev[key]),
    }));
  };

  const [
    numericValueAcceptableObservations,
    setNumericValueAcceptableObservations,
  ] = useState([]);

  const constructedObservationsRef = useRef([]);

  const auth = useSelector((state) => state.jwtAuthreducer);

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  // const onSuccess = () => {
  //   const document = true;
  //   const message = rowData
  //     ? "Document updated successfully!"
  //     : "Document created successfully!";
  //   showAlert(message, "success");
  //   // refetchPayment();
  //   // refetchBussinessReport();
  //   onObservationValueClose(false);
  //   onClose(document);
  // };

  const onSuccess = (groupedData = {}) => {
    const document = true;
    const message = rowData
      ? "Document updated successfully!"
      : "Document created successfully!";
    showAlert(message, "success");

    const ids = Object.values(groupedData)
      .flat()
      .map((entry) => ({
        appointment_id: entry.appointment_id,
        investigation_id: entry.investigation_id,
      }));

    onObservationValueClose(false, ids);
    onClose(document);
  };

  const onError = (error) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong!";

    showAlert(errorMessage, "danger");
  };

  const {
    data: listPayment,
    isLoading: isLoadingPayment,
    refetch: refetchPayment,
  } = useQuery(
    [
      "fetch-observation-result-entry",
      rowData?.appointment_id,
      investigationId,
    ],
    () =>
      fetchObservationResultEntryData({
        appointment_id: rowData?.appointment_id,
        investigation_id: investigationId,
      }),
    {
      staleTime: 0,
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
          }));
        }
        return res;
      },
      onError: (error) => {
        console.error("Error fetching payments:", error);
        Swal.fire({
          icon: "error",
          title: "Failed to Fetch Data",
          text:
            error?.response?.data?.detail ||
            "Something went wrong while fetching payment data.",
        });
      },
      enabled: !!rowData?.appointment_id && !!investigationId,
    },
  );

  const hasAnyCulture = useMemo(() => {
    if (
      !observationDataRedux ||
      Object.keys(observationDataRedux).length === 0
    ) {
      return false;
    }

    return Object.entries(observationDataRedux).some(
      ([department, investigations]) =>
        Object.entries(investigations).some(
          ([investigation_name, observationWrapper]) =>
            observationWrapper?.investigation_detail?.is_culture === true,
        ),
    );
  }, [observationDataRedux]);

  const isInvestigationCulture = (department, investigationName) => {
    if (!observationDataRedux || !observationDataRedux[department]) {
      return false;
    }

    const investigation = observationDataRedux[department][investigationName];
    return investigation?.investigation_detail?.is_culture === true;
  };

  const {
    data: onlyOrganismData,
    isLoading: isLoadingOnlyOrganism,
    refetch: refetchOnlyOrganismDropDown,
  } = useQuery(["fetch-only-organism-data"], () => fetchOnlyOrganismData(), {
    staleTime: STALE_TIME,
    select: (res) => {
      const response = res?.data?.data;
      if (response && response.length) {
        res.data.data = response.map((x, index) => ({
          ...x,
        }));
      }
      return res;
    },
    onError: (error) => {
      console.error("Error fetching payments:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Fetch Data",
        text:
          error?.response?.data?.detail ||
          "Something went wrong while fetching payment data.",
      });
    },
    enabled: hasAnyCulture,
  });

  useEffect(() => {
    selectedInvestigationIds.forEach(
      async ({ investigationKey, investigationId }) => {
        if (hasAnyCulture) {
          try {
            const res = await fetchInvestigationObservationDropDown({
              investigation_id: investigationId,
              req_help_data: true,
            });

            const response = res?.data?.data || [];

            setInvestigationDataMap((prevData) => {
              const existingData =
                prevData[investigationKey]?.[investigationId] || [];

              // Create a map of existing data by observation_id to preserve values
              const existingDataMap = new Map();
              existingData.forEach((item) => {
                existingDataMap.set(item.observation_id, item);
              });

              // Get current observationData to preserve user-entered values
              // Use ref to access current value without causing dependency issues
              const obsDataMap = new Map();
              observationDataRef.current.forEach((item) => {
                if (
                  String(item.parent_investigation_id) ===
                  String(investigationId)
                ) {
                  obsDataMap.set(item.id, item);
                }
              });

              // Merge response with existing data, preserving observation values
              const mergedData = response.map((item) => {
                const existing = existingDataMap.get(item.observation_id);
                const obsData = obsDataMap.get(item.observation_id);

                // Prefer observationData values, then existing data, then response
                const observationValue =
                  obsData?.observation_value !== undefined &&
                  obsData?.observation_value !== null &&
                  String(obsData.observation_value).trim() !== ""
                    ? obsData.observation_value
                    : existing?.observation_value !== undefined &&
                        existing?.observation_value !== null &&
                        String(existing.observation_value).trim() !== ""
                      ? existing.observation_value
                      : item.observation_value;

                const observationValueMacReading =
                  obsData?.observation_value_mac_reading !== undefined &&
                  obsData?.observation_value_mac_reading !== null &&
                  String(obsData.observation_value_mac_reading).trim() !== ""
                    ? obsData.observation_value_mac_reading
                    : existing?.observation_value_mac_reading !== undefined &&
                        existing?.observation_value_mac_reading !== null &&
                        String(
                          existing.observation_value_mac_reading,
                        ).trim() !== ""
                      ? existing.observation_value_mac_reading
                      : item.observation_value_mac_reading;

                return {
                  ...item,
                  observation_value: observationValue,
                  observation_value_mac_reading: observationValueMacReading,
                };
              });

              // Add any existing items that aren't in the response
              const existingIds = new Set(
                mergedData.map((item) => item.observation_id),
              );
              existingData.forEach((item) => {
                if (!existingIds.has(item.observation_id)) {
                  mergedData.push(item);
                }
              });

              const uniqueData = mergedData.filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.investigation_id === item.investigation_id &&
                      t.observation_id === item.observation_id,
                  ),
              );

              return {
                ...prevData,
                [investigationKey]: {
                  ...prevData[investigationKey],
                  [investigationId]: uniqueData,
                },
              };
            });
          } catch (error) {
            console.error("Error fetching investigation data:", error);
          }
        }
      },
    );
  }, [selectedInvestigationIds, hasAnyCulture]);

  const handleInvestigationChange = (event, investigationKey) => {
    const selectedId = event.target.value;
    if (!selectedId) return;

    const selectedOption = onlyOrganismData?.data?.data?.find(
      (item) => item.id == selectedId,
    );
    if (!selectedOption) return;

    setSelectedInvestigations((prev) => {
      const existingList = prev[investigationKey] || [];
      if (existingList.some((item) => item.id == selectedId)) {
        Swal.fire({
          icon: "warning",
          title: "Already Selected",
          text: "This investigation is already added!",
        });
        return prev;
      }
      return {
        ...prev,
        [investigationKey]: [
          ...existingList,
          { id: selectedOption.id, name: selectedOption.investigation_name },
        ],
      };
    });

    setSelectedInvestigationIds((prev) => [
      ...prev,
      { investigationKey, investigationId: selectedOption.id },
    ]);
  };

  const handleRemoveInvestigation = (investigationKey, id) => {
    setSelectedInvestigations((prev) => ({
      ...prev,
      [investigationKey]:
        prev[investigationKey]?.filter((item) => item.id !== id) || [],
    }));

    setInvestigationDataMap((prev) => {
      const updated = { ...prev };
      if (updated[investigationKey]) {
        delete updated[investigationKey][id];
      }
      return updated;
    });

    setObservationData((prevData) => {
      const updated = prevData.filter(
        (item) => String(item.parent_investigation_id) !== String(id),
      );
      observationDataRef.current = updated;
      return updated;
    });

    setSelectedInvestigationIds((prev) =>
      prev.filter(
        (item) =>
          !(
            item.investigationKey === investigationKey &&
            item.investigationId === id
          ),
      ),
    );

    setRemovedInvestigationIds((prev) => [...prev, { investigationKey, id }]);
  };

  const inputRefs = useRef([]);
  const inputRefsDrug = useRef([]);
  const lastFocusedInput = useRef(null);

  // useEffect(() => {
  //   if (
  //     isOpenObservationValue &&
  //     listPayment?.data?.observations?.department_investigation
  //   ) {
  //     dispatch(
  //       setResultEntryObservations(
  //         listPayment?.data?.observations?.department_investigation,
  //       ),
  //     );
  //   }
  // }, [
  //   isOpenObservationValue,
  //   listPayment?.data?.observations?.department_investigation,
  // ]);

  useEffect(() => {
    if (isOpenObservationValue) {
      // Pehle reset karo
      dispatch(resetResultEntryObservations());

      // Phir naya data set karo agar available ho
      if (listPayment?.data?.observations?.department_investigation) {
        dispatch(
          setResultEntryObservations(
            listPayment?.data?.observations?.department_investigation,
          ),
        );
      }
    }
  }, [
    isOpenObservationValue,
    listPayment?.data?.observations?.department_investigation,
  ]);

  useEffect(() => {
    let observationOrganismData = [];

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            if (
              observationWrapper?.investigation_organism &&
              observationWrapper.investigation_organism.length > 0
            ) {
              observationOrganismData = [
                ...observationOrganismData,
                ...observationWrapper.investigation_organism.map((item) => ({
                  ...item,
                  investigationKey: `${department}-${investigation_name}`,
                })),
              ];
            }
          },
        );
      },
    );

    // Check if user has manually added investigations using ref to avoid stale closure
    const hasManuallyAddedInvestigations = Object.keys(
      selectedInvestigationsRef.current,
    ).some(
      (key) =>
        selectedInvestigationsRef.current[key] &&
        selectedInvestigationsRef.current[key].length > 0,
    );

    if (observationOrganismData.length > 0) {
      setIsOrganismData(true);
      setOrganismObservations(observationOrganismData);

      // Group by investigationKey and investigationId
      const investigationMap = {};
      observationOrganismData.forEach((item) => {
        const key = `${item.investigationKey}-${item.investigation_id}`;
        if (!investigationMap[key]) {
          investigationMap[key] = {
            investigationKey: item.investigationKey,
            investigationId: item.investigation_id,
          };
        }
      });

      const selectedIds = Object.values(investigationMap);

      // Only update selectedInvestigationIds if we have new data from observationDataRedux
      // Otherwise preserve manually added ones
      setSelectedInvestigationIds((prev) => {
        // Merge with existing manually added investigations
        const existingIds = new Set(
          prev.map(
            (item) => `${item.investigationKey}-${item.investigationId}`,
          ),
        );
        const newIds = selectedIds.filter(
          (item) =>
            !existingIds.has(
              `${item.investigationKey}-${item.investigationId}`,
            ),
        );
        return [...prev, ...newIds];
      });

      let allObservationData = [];

      // Process each unique investigationKey and investigationId combination
      Object.values(investigationMap).forEach(
        ({ investigationKey, investigationId }) => {
          const investigationData = observationOrganismData.filter(
            (item) =>
              item.investigationKey === investigationKey &&
              item.investigation_id === investigationId,
          );

          const investigationInfo = onlyOrganismData?.data?.data?.find(
            (item) => item.id === investigationId,
          );

          if (investigationInfo) {
            setSelectedInvestigations((prev) => {
              const existingList = prev[investigationKey] || [];
              if (!existingList.some((inv) => inv.id === investigationId)) {
                return {
                  ...prev,
                  [investigationKey]: [
                    ...existingList,
                    {
                      id: investigationId,
                      name: investigationInfo.investigation_name,
                    },
                  ],
                };
              }
              return prev;
            });

            setInvestigationDataMap((prev) => {
              const existingData =
                prev[investigationKey]?.[investigationId] || [];
              const combined = [...existingData, ...investigationData];

              const uniqueData = combined.filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.investigation_id === item.investigation_id &&
                      t.observation_id === item.observation_id,
                  ),
              );

              const currentObservationData = uniqueData
                .filter(
                  (item) =>
                    (item.observation_value !== undefined &&
                      item.observation_value !== null &&
                      String(item.observation_value).trim() !== "") ||
                    (item.observation_value_mac_reading !== undefined &&
                      item.observation_value_mac_reading !== null &&
                      String(item.observation_value_mac_reading).trim() !== ""),
                )
                .map((item) => ({
                  id: item.observation_id,
                  parent_investigation_id: item.investigation_id,
                  observation_value: item.observation_value || "",
                  observation_value_mac_reading:
                    item.observation_value_mac_reading || "",
                }));

              allObservationData = [
                ...allObservationData,
                ...currentObservationData,
              ];

              return {
                ...prev,
                [investigationKey]: {
                  ...prev[investigationKey],
                  [investigationId]: uniqueData,
                },
              };
            });
          }
        },
      );

      // Remove duplicates from allObservationData
      allObservationData = allObservationData.filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              String(t.id) === String(item.id) &&
              String(t.parent_investigation_id) ===
                String(item.parent_investigation_id),
          ),
      );

      // Merge with existing observationData to preserve manually entered values
      setObservationData((prevObsData) => {
        const merged = [...prevObsData];
        allObservationData.forEach((newItem) => {
          const existingIndex = merged.findIndex(
            (item) =>
              String(item.id) === String(newItem.id) &&
              String(item.parent_investigation_id) ===
                String(newItem.parent_investigation_id),
          );
          if (existingIndex === -1) {
            merged.push(newItem);
          }
        });
        observationDataRef.current = merged;
        return merged;
      });
    } else {
      // Only reset if there are no manually added investigations
      // This prevents clearing user-added investigations when observationDataRedux updates
      if (!hasManuallyAddedInvestigations) {
        setIsOrganismData(false);
        setOrganismObservations([]);
        setSelectedInvestigationIds([]);
        setInvestigationDataMap({});
        setObservationData([]);
        observationDataRef.current = [];
      }
    }
  }, [observationDataRedux, onlyOrganismData]);

  const {
    data: reportRoleData,
    isLoading,
    refetch: refetchReportRole,
  } = useQuery(
    ["fetch-report-role", jwtAuthreducer.user.employee_code_id],
    () =>
      fetchReportRoleById({
        employee_code_id: jwtAuthreducer.user.employee_code_id,
      }),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      select: (res) => {
        return res;
      },
      onError: (res) => {
        console.error("Error fetching areas:", res);
      },
    },
  );

  const { mutate: addPatientLabResultEntryMutate, isLoading: isLoadingAdd } =
    useMutation({
      mutationFn: createPatientLabResultEntry,
      onSuccess,
      onError,
    });

  const updateRow = useCallback((originalRow, rowId, field, value) => {
    setUpdatedRows((prev) => {
      const newUpdatedRows = {
        ...prev,
        [rowId]: {
          ...originalRow,
          [field]: value,
        },
      };
      setIsSaveEnabled(Object.keys(newUpdatedRows).length > 0);
      return newUpdatedRows;
    });
  }, []);

  const handleSave = (currentRole) => {
    if (
      !observationDataRedux ||
      Object.keys(observationDataRedux).length === 0
    ) {
      console.warn("No data available to save.");
      return;
    }

    if (currentRole === "APPROVED" && !filterValue && !doctorId) {
      return;
    }

    let hasMandatoryUnfilled = false;
    let mandatoryInvestigations = [];

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            const investigationKey = `${department}-${investigation_name}`;

            if (checkedInvestigations[investigationKey]) {
              const observations = observationWrapper?.observations || [];

              const unfilledMandatoryObs = observations
                .filter(
                  (obs) =>
                    obs.mark_as_mandatory === true &&
                    obs.header !== true &&
                    (obs.observation_value === undefined ||
                      obs.observation_value === null ||
                      obs.observation_value === ""),
                )
                .map((obs) => obs.observation_name || "Unnamed Observation");

              if (unfilledMandatoryObs.length > 0) {
                hasMandatoryUnfilled = true;
                mandatoryInvestigations.push({
                  name: investigation_name,
                  observations: unfilledMandatoryObs,
                });
              }
            }
          },
        );
      },
    );

    if (hasMandatoryUnfilled) {
      const formattedList = mandatoryInvestigations
        .map(
          (inv) =>
            `<b>${inv.name}</b><br>` +
            inv.observations
              .map((obs) => `<span style="color:red;">• ${obs}</span>`)
              .join("<br>"),
        )
        .join("<br><br>");

      Swal.fire({
        title: "Mandatory Fields Missing!",
        html: `
        The following investigations have mandatory observations not filled:<br><br>
        ${formattedList}<br><br>
        These fields are required. Do you want to proceed without filling them?
      `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Proceed Anyway",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          proceedToSave(currentRole);
        } else {
          console.log("Save cancelled by user due to mandatory fields.");
        }
      });

      return;
    }

    proceedToSave(currentRole);
  };

  const proceedToSave = (currentRole) => {
    const groupedData = {};
    let patientNameId = 0;
    let appointmentInvestigationId = 0;

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            const investigationKey = `${department}-${investigation_name}`;

            if (!checkedInvestigations[investigationKey]) {
              console.log(
                `Skipping unchecked investigation: ${investigationKey}`,
              );
              return;
            }

            const observations = observationWrapper?.observations || [];
            const key = `${department}-${investigation_name}`;
            const currentComment = commentsMap[key] || "";
            const currentCommentName = commentNamesMap[key] || null;
            const currentTemplate = template[investigationKey] || {};

            const hasAtLeastOneFilledValue = observations.some(
              (obs) =>
                obs.header !== true &&
                obs.observation_value !== undefined &&
                obs.observation_value !== null &&
                obs.observation_value !== "",
            );

            if (!currentTemplate?.template_id && !hasAtLeastOneFilledValue) {
              console.log(
                `Skipping investigation (all values empty): ${investigationKey}`,
              );
              return;
            }

            const allFilled = observations.every(
              (obs) =>
                obs.header === true ||
                (obs.observation_value !== undefined &&
                  obs.observation_value !== null &&
                  obs.observation_value !== ""),
            );
            const currentStatus = allFilled ? "RESULT_DONE" : "INCOMPLETE";

            observations.forEach((obs, obsIndex) => {
              if (obs.patient_details?.id)
                patientNameId = obs.patient_details.id;
              if (obs.appointment_investigation_id)
                appointmentInvestigationId = obs.appointment_investigation_id;

              const appointment_id = obs.appointment_id || 0;
              const investigation_id =
                obs.investigation_details?.id || obs.investigation_id || 0;

              if (!groupedData[appointment_id])
                groupedData[appointment_id] = [];

              let investigationEntry = groupedData[appointment_id].find(
                (entry) => entry.investigation_id === investigation_id,
              );

              if (!investigationEntry) {
                investigationEntry = {
                  investigation_id,
                  appointment_id,
                  company_id: companyLocalStorageId(),
                  investigationKey,
                  observation: [],
                  observation_organism: [],
                  deleted_observation_organism: [],
                };
                groupedData[appointment_id].push(investigationEntry);
              }

              investigationEntry.observation.push({
                observation_id: obs.observation_id,
                observation_value:
                  obs.observation_value === "" ||
                  obs.observation_value === undefined ||
                  obs.observation_value === null
                    ? null
                    : String(obs.observation_value),
                status: currentRole === "SAVE" ? currentStatus : currentRole,
                min_reading: obs.min_reading,
                max_reading: obs.max_reading,
                min_critical: obs.min_critical,
                max_critical: obs.max_critical,
                unit: obs.unit,
                doctor_id: doctorId || filterValue,
                comments: obsIndex === 0 ? currentComment : null,
                comment_id:
                  obsIndex === 0 ? currentCommentName?.comment_id : null,
                modified_by: auth?.user?.employee_code_id,
                template_id: currentTemplate?.template_id,
                template_description: currentTemplate?.template_description,
                display_reading: obs.display_reading,
                 company_id: companyLocalStorageId(),
              });
            });
          },
        );
      },
    );

    if (observationData && observationData.length > 0) {
      Object.entries(selectedInvestigations).forEach(
        ([investigationKey, invList]) => {
          if (!invList || invList.length === 0) return;

          const selectedIds = invList.map((inv) => String(inv.id));

          const filteredOrganisms = observationData.filter((obsData) => {
            return selectedIds.includes(
              String(obsData.parent_investigation_id),
            );
          });

          if (filteredOrganisms.length === 0) return;

          let investigationEntry = Object.values(groupedData)
            .flat()
            .find((entry) => entry.investigationKey === investigationKey);

          if (!investigationEntry) {
            const firstDashIndex = investigationKey.indexOf("-");
            const department = investigationKey.substring(0, firstDashIndex);
            const investigation_name = investigationKey.substring(
              firstDashIndex + 1,
            );
            const observationWrapper =
              observationDataRedux[department]?.[investigation_name];

            if (
              observationWrapper &&
              observationWrapper.observations?.length > 0
            ) {
              const firstObs = observationWrapper.observations[0];
              const appointment_id = firstObs.appointment_id || 0;
              const investigation_id =
                firstObs.investigation_details?.id ||
                firstObs.investigation_id ||
                0;

              if (firstObs.patient_details?.id)
                patientNameId = firstObs.patient_details.id;
              if (firstObs.appointment_investigation_id)
                appointmentInvestigationId =
                  firstObs.appointment_investigation_id;

              if (!groupedData[appointment_id])
                groupedData[appointment_id] = [];

              investigationEntry = {
                investigation_id,
                appointment_id,
                investigationKey,
                observation: [],
                observation_organism: [],
                deleted_observation_organism: [],
              };
              groupedData[appointment_id].push(investigationEntry);
            } else {
              console.log(
                `Cannot create entry for investigationKey: ${investigationKey} - no observation data found`,
              );
              return;
            }
          }

          if (!investigationEntry.observation_organism) {
            investigationEntry.observation_organism = [];
          }
          if (!investigationEntry.deleted_observation_organism) {
            investigationEntry.deleted_observation_organism = [];
          }

          filteredOrganisms.forEach((obsData) => {
            investigationEntry.observation_organism.push({
              patient_name_id: patientNameId,
              observation_id: obsData.id,
              investigation_id: obsData?.parent_investigation_id,
              parent_investigation_id: investigationEntry.investigation_id,
              appointment_id: investigationEntry.appointment_id,
              franchise_id: Number(localStorage.getItem("franchise_id")),
              zone_id: zoneLocalStorageId(),
              appointment_investigation_id: appointmentInvestigationId,
              gender: "",
              observation_value:
                obsData.observation_value === "" ||
                obsData.observation_value === undefined ||
                obsData.observation_value === null
                  ? null
                  : String(obsData.observation_value),
              observation_value_mac_reading:
                obsData.observation_value_mac_reading === "" ||
                obsData.observation_value_mac_reading === undefined ||
                obsData.observation_value_mac_reading === null
                  ? null
                  : String(obsData.observation_value_mac_reading),
              min_reading: "",
              max_reading: "",
              min_critical: "",
              max_critical: "",
              unit: "",
              is_active: true,
              created_by: auth?.user?.employee_code_id || 0,
              company_id: companyLocalStorageId(),
            });
          });
        },
      );
    }

    removedInvestigationIds.forEach(({ investigationKey, id }) => {
      const allEntries = Object.values(groupedData).flat();

      let investigationEntry = allEntries.find(
        (entry) => entry.investigationKey === investigationKey,
      );

      if (!investigationEntry) {
        const firstDashIndex = investigationKey.indexOf("-");
        const department = investigationKey.substring(0, firstDashIndex);
        const investigation_name = investigationKey.substring(
          firstDashIndex + 1,
        );
        const observationWrapper =
          observationDataRedux[department]?.[investigation_name];

        if (observationWrapper && observationWrapper.observations?.length > 0) {
          const firstObs = observationWrapper.observations[0];
          const appointment_id = firstObs.appointment_id || 0;
          const investigation_id =
            firstObs.investigation_details?.id ||
            firstObs.investigation_id ||
            0;

          if (firstObs.patient_details?.id)
            patientNameId = firstObs.patient_details.id;
          if (firstObs.appointment_investigation_id)
            appointmentInvestigationId = firstObs.appointment_investigation_id;

          if (!groupedData[appointment_id]) groupedData[appointment_id] = [];

          investigationEntry = {
            investigation_id,
            appointment_id,
            investigationKey,
            observation: [],
            observation_organism: [],
            deleted_observation_organism: [],
          };
          groupedData[appointment_id].push(investigationEntry);
        }
      }

      if (investigationEntry) {
        if (!investigationEntry.deleted_observation_organism) {
          investigationEntry.deleted_observation_organism = [];
        }

        investigationEntry.deleted_observation_organism.push({
          investigation_id: id,
          parent_investigation_id: investigationEntry.investigation_id,
        });

        console.log(
          `✅ Added removed investigation ${id} to deleted_observation_organism for key: ${investigationKey}`,
        );
      } else {
        console.warn(
          `⚠️ Could not find or create entry for removed investigationKey: ${investigationKey}`,
        );
      }
    });

    const payload = {
      data: Object.values(groupedData)
        .flat()
        .map(({ investigationKey, ...rest }) => ({
          ...rest,
          deleted_observation_organism: rest.deleted_observation_organism || [],
        })),
    };

    if (payload.data.length === 0) {
      console.warn("No checked investigations to save.");
      return;
    }

    addPatientLabResultEntryMutate(payload, {
      onSuccess: () => onSuccess(groupedData),
      onError,
    });
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const handleValueChange = useCallback(
    (value, department, investigation, obsId) => {
      const currentConstructedObservations = constructedObservationsRef.current;
      const isDirectChange =
        currentConstructedObservations.findIndex((o) => +o.id === +obsId) > -1;

      if (
        isDirectChange &&
        value?.length &&
        (value[value.length - 1] === "." ||
          (value.includes(".") && value[value.length - 1] === "0"))
      ) {
        const isNumericValue = /^\d+\.?\d*$/.test(value.replace(/\.+$/, ""));
        if (isNumericValue) {
          return;
        }
      }

      setFilledObservations((prev) => {
        const obsIndex = prev.findIndex((o) => +o.id === +obsId);
        const formulaIndex = currentConstructedObservations.findIndex(
          (o) => +o.id === +obsId,
        );
        const obs = { id: obsId, value: +value };
        if (formulaIndex > -1) {
          obs.isDirectChange = true;
        }

        if (obsIndex > -1) {
          if (!value) {
            return prev.filter((_, index) => index !== obsIndex);
          } else {
            return prev.map((o, index) => (index === obsIndex ? obs : o));
          }
        } else {
          return [...prev, obs];
        }
      });

      dispatch(
        updateResultEntryObservation(department, investigation, obsId, value),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (!constructedObservations.length) {
      const constructedObs = constructFormulaFieldsFromObservations({
        department_investigation: observationDataRedux,
      });
      setConstructedObservations(constructedObs);
      constructedObservationsRef.current = constructedObs;
    }
  }, [observationDataRedux]);

  useEffect(() => {
    const numericValueObservations = [];
    for (const obs of constructedObservations) {
      const constructedObervationIds = obs.fields.map(
        (f) => +f.replace(/[#@]/g, ""),
      );
      numericValueObservations.push(...constructedObervationIds);
    }
    const uniqueNumericValueObservations = [
      ...new Set(numericValueObservations),
    ];
    setNumericValueAcceptableObservations(uniqueNumericValueObservations);
  }, [constructedObservations]);

  useEffect(() => {
    // console.table(filledObservations);
    // console.table(constructedObservations);
    const formulas = constructFormulasForFilledValues(
      filledObservations,
      constructedObservations,
    );
    setUpdatedFormulas((prev) => {
      const newIds = new Set(formulas.map((f) => f.id));
      const removedFormulas = prev.filter((f) => !newIds.has(f.id));
      removedFormulas.forEach(({ department, investigation, id }) => {
        dispatch(
          updateResultEntryObservation(department, investigation, id, ""),
        );
      });

      return formulas;
    });
  }, [filledObservations]);

  useEffect(() => {
    updatedFormulas.forEach((obs) => {
      const { department, investigation, id, observationValue } = obs;

      let value = parseFloat(Number(observationValue));
      let isValueRemoved = false;
      if (isNaN(value)) isValueRemoved = true;

      const formattedValue = +(value < 0 ? value * -1 : value).toFixed(2);

      setFilledObservations((prev) => {
        const obsIndex = prev.findIndex((o) => +o.id === +id);
        const obs = { id, value: formattedValue };
        let updatedFilledObservations;
        if (obsIndex > -1) {
          if (isValueRemoved) {
            updatedFilledObservations = prev.filter(
              (_, index) => index !== obsIndex,
            );
          } else {
            updatedFilledObservations = prev.map((o, index) => {
              // return index === obsIndex ? obs : o;
              if (index === obsIndex) {
                if (o?.isDirectChange) {
                  return o;
                }
                return obs;
              }
              return o;
            });
          }
        } else {
          updatedFilledObservations = [...prev, obs];
        }

        if (JSON.stringify(prev) === JSON.stringify(updatedFilledObservations))
          return prev;
        return updatedFilledObservations;
      });

      dispatch(
        updateResultEntryObservation(
          department,
          investigation,
          id,
          formattedValue,
        ),
      );
    });
  }, [updatedFormulas]);

  const add_observation_columns = useMemo(() => {
    return ADD_OBSERVATION_COLUMNS_DATA(
      handleValueChange,
      inputRefs,
      numericValueAcceptableObservations,
      checkedInvestigations,
      lastFocusedInput,
    );
  }, [inputRefs, checkedInvestigations, lastFocusedInput]);

  const handleValue = (
    rowId,
    newValue,
    parent_investigation_id,
    fieldType = "observation_value",
  ) => {
    setObservationData((prevData) => {
      if (!prevData || prevData.length === 0) {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          observation_value: fieldType === "observation_value" ? newValue : "",
          observation_value_mac_reading:
            fieldType === "observation_value_mac_reading" ? newValue : "",
        };
        observationDataRef.current = [newRow];
        return [newRow];
      }

      const rowExists = prevData.some(
        (row) =>
          String(row.id) === String(rowId) &&
          String(row.parent_investigation_id) ===
            String(parent_investigation_id),
      );

      let updatedData;
      if (rowExists) {
        updatedData = prevData.map((row) => {
          if (
            String(row.id) === String(rowId) &&
            String(row.parent_investigation_id) ===
              String(parent_investigation_id)
          ) {
            return {
              ...row,
              [fieldType]: newValue,
              parent_investigation_id: parent_investigation_id,
              // Preserve the other field if it exists
              observation_value:
                fieldType === "observation_value"
                  ? newValue
                  : row.observation_value || "",
              observation_value_mac_reading:
                fieldType === "observation_value_mac_reading"
                  ? newValue
                  : row.observation_value_mac_reading || "",
            };
          }
          return row;
        });
      } else {
        const newRow = {
          id: rowId,
          parent_investigation_id: parent_investigation_id,
          observation_value: fieldType === "observation_value" ? newValue : "",
          observation_value_mac_reading:
            fieldType === "observation_value_mac_reading" ? newValue : "",
        };
        updatedData = [...prevData, newRow];
      }
      observationDataRef.current = updatedData;
      return updatedData;
    });
  };

  const investigation_observation_columns = useMemo(() => {
    return INVESTIGATION_OBSERVATION_COLUMNS_DATA(
      handleValue,
      inputRefs,
      lastFocusedInput,
    );
  }, [inputRefs]);

  const handleCommentChange = (departmentName, investigationName, value) => {
    const key = `${departmentName}-${investigationName}`;
    setCommentsMap((prevMap) => ({
      ...prevMap,
      [key]: value,
    }));
  };

  // Handle comment name selection for a specific investigation
  const handleCommentNameChange = (
    departmentName,
    investigationName,
    selectedCommentName,
  ) => {
    const key = `${departmentName}-${investigationName}`;

    if (selectedCommentName === null) {
      // Handle clearing the dropdown
      setCommentNamesMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });

      setCommentsMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[key];
        return newMap;
      });
      return;
    }

    setCommentNamesMap((prevMap) => ({
      ...prevMap,
      [key]: selectedCommentName,
    }));

    if (selectedCommentName?.comments) {
      setCommentsMap((prevMap) => ({
        ...prevMap,
        [key]: selectedCommentName.comments,
      }));
    }
  };

  useEffect(() => {
    if (!observationDataRedux || Object.keys(observationDataRedux).length === 0)
      return;

    const newCommentsMap = {};
    const newCommentNamesMap = {};
    const extractedDoctors = {};
    const newTemplateMap = {};

    Object.entries(observationDataRedux).forEach(
      ([department, investigations]) => {
        Object.entries(investigations).forEach(
          ([investigation_name, observationWrapper]) => {
            const key = `${department}-${investigation_name}`;
            const observations = observationWrapper?.observations || [];

            if (observations && observations.length > 0) {
              if (observationWrapper?.comments) {
                newCommentsMap[key] = observationWrapper.comments;
              } else {
                const firstObsWithComment = observations.find(
                  (obs) => obs?.comments,
                );

                newCommentsMap[key] = firstObsWithComment?.comments || "";
              }

              if (observationWrapper?.comment_obj) {
                newCommentNamesMap[key] = {
                  comment_id: observationWrapper.comment_obj.id,
                  comment_name: observationWrapper.comment_obj.comment_name,
                  comments: observationWrapper.comment_obj.comments,
                };
              } else {
                const obsWithCommentId = observations.find(
                  (obs) => obs?.comment_id,
                );
                if (obsWithCommentId) {
                  newCommentNamesMap[key] = {
                    comment_id: obsWithCommentId.comment_id,
                    comment_name: obsWithCommentId.comment_name || "",
                    comments: obsWithCommentId.comments || "",
                  };
                }
              }

              observations.forEach((obs) => {
                if (obs?.doctor_obj?.id) {
                  extractedDoctors[key] = obs.doctor_obj.id;
                }
              });

              const template_id =
                observationWrapper?.template_id || observations[0]?.template_id;
              const template_description =
                observationWrapper?.template_description ||
                observations[0]?.template_description ||
                "";

              if (template_id || template_description) {
                newTemplateMap[key] = {
                  template_id,
                  template_description,
                };
              }
            }
          },
        );
      },
    );

    setCommentsMap(newCommentsMap);
    setCommentNamesMap(newCommentNamesMap);
    setTemplate(newTemplateMap);

    const firstDoctorId = Object.values(extractedDoctors)[0];
    if (firstDoctorId) {
      setdoctorId(firstDoctorId);
    }
  }, [observationDataRedux]);

  if (isLoadingPayment) {
    return <Loader isLoading={isLoadingPayment} />;
  }

  return (
    <>
      <div style={{ position: "relative" }}>
        {(isLoadingPayment || isLoading || isLoadingAdd) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <Loader isLoading={true} />
          </div>
        )}

        <TitleComponent headerDetails={listPayment?.data?.header_details} />
        <div className="d-flex justify-content-between align-items-center mb-1">
          <InvestigationResults
            extraInvestigations={listPayment?.data?.extra_investigation}
            observations={listPayment?.data?.observations}
            setInvestigationId={setInvestigationId}
          />
        </div>

        {observationDataRedux &&
          Object.keys(observationDataRedux).length > 0 &&
          Object.entries(observationDataRedux).map(
            ([department, investigations]) =>
              Object.entries(investigations).map(
                ([investigation_name, observationWrapper], index) => {
                  const investigationKey = `${department}-${investigation_name}`;
                  const observations = observationWrapper?.observations || [];
                  const investigationIds =
                    observations[0]?.investigation_details?.id || null;

                  return (
                    <div
                      key={`${department}-${investigation_name}`}
                      style={{ marginBottom: 20 }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center p-2 mb-2"
                        style={{ background: "#afeeee" }}
                      >
                        <p style={{ fontSize: "16px", fontWeight: "bold" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(
                              checkedInvestigations[investigationKey],
                            )}
                            onChange={() =>
                              handleCheckboxChange(investigationKey)
                            }
                            style={{ marginRight: 8 }}
                          />
                          {index + 1}. {investigation_name}
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleDocuments}
                        >
                          Add Attachment
                        </button>
                        {documentInfo?.data?.file_name && (
                          <span
                            style={{
                              fontSize: "15px",
                              fontWeight: "bold",
                              padding: "5px",
                              marginLeft: "10px",
                            }}
                          >
                            {documentInfo?.data?.file_name}
                          </span>
                        )}
                      </div>
                      {observations?.map((observation, obsIndex) => {
                        const reportType =
                          observation?.investigation_details?.report_type;

                        return (
                          <div key={obsIndex} style={{ marginBottom: "10px" }}>
                            {reportType === "Path RichText" ? (
                              <>
                                <div className="col-md-4">
                                  <h5
                                    style={{
                                      fontSize: "13px",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    Investigation Template
                                  </h5>
                                  <TemplateDropDown
                                    investigationId={investigationIds}
                                    setFilterValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          ...val,
                                        },
                                      }))
                                    }
                                    template={template[investigationKey] || {}}
                                  />
                                </div>
                                <div className="form-group mt-2">
                                  <label
                                    htmlFor="commentDesc"
                                    className="form-label"
                                  >
                                    Template Description
                                  </label>
                                  <TextEditor
                                    editorValue={
                                      template[investigationKey]
                                        ?.template_description || ""
                                    }
                                    height={400}
                                    setEditorValue={(val) =>
                                      setTemplate((prev) => ({
                                        ...prev,
                                        [investigationKey]: {
                                          ...prev[investigationKey],
                                          template_description: val,
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            ) : (
                              <div key={obsIndex}>
                                {obsIndex === 0 && (
                                  <Table
                                    data={observations}
                                    tableColumns={add_observation_columns}
                                    showPagination={false}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {isInvestigationCulture(
                        department,
                        investigation_name,
                      ) && (
                        <>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: "100px",
                              marginTop: "10px",
                            }}
                          >
                            <select
                              style={{ width: "100%", padding: "5px" }}
                              onChange={(e) => {
                                handleInvestigationChange(e, investigationKey);
                                e.target.value = "";
                              }}
                            >
                              <option value="">- Select -</option>
                              {onlyOrganismData?.data?.data?.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.investigation_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {(selectedInvestigations[investigationKey] || []).map(
                            (investigation) => (
                              <div
                                key={investigation.id}
                                style={{
                                  marginBottom: "20px",
                                  marginTop: "20px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "5px",
                                    border: "1px solid #ccc",
                                    borderRadius: "5px",
                                    backgroundColor: "#f5f5f5",
                                    maxWidth: "400px",
                                  }}
                                >
                                  <strong>{investigation.name}</strong>

                                  <button
                                    onClick={() =>
                                      handleRemoveInvestigation(
                                        investigationKey,
                                        investigation.id,
                                      )
                                    }
                                    style={{
                                      background: "red",
                                      color: "white",
                                      border: "none",
                                      padding: "3px 6px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>

                                {investigationDataMap[investigationKey]?.[
                                  investigation.id
                                ] && (
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      padding: "5px",
                                      border: "1px solid #ddd",
                                      borderRadius: "5px",
                                    }}
                                  >
                                    <Table
                                      data={
                                        investigationDataMap[
                                          investigationKey
                                        ]?.[investigation.id]
                                      }
                                      tableColumns={
                                        investigation_observation_columns
                                      }
                                      showPagination={false}
                                    />
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </>
                      )}
                      <div className="row mt-2">
                        <div className="col-md-4">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "8px",
                            }}
                          >
                            <h5 style={{ fontSize: "13px", margin: 0 }}>
                              Investigation Comments Name
                            </h5>

                            <button
                              onClick={() => setShowEditor(!showEditor)}
                              style={{
                                backgroundColor: showEditor
                                  ? "#1890ff"
                                  : "#e6f7ff",
                                border: "1px solid #1890ff",
                                borderRadius: 6,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 10px",
                                fontSize: 13,
                                color: showEditor ? "#fff" : "#1890ff",
                                fontWeight: "bold",
                                transition: "all 0.2s ease-in-out",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  showEditor ? "#40a9ff" : "#bae7ff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  showEditor ? "#1890ff" : "#e6f7ff";
                              }}
                            >
                              {showEditor ? (
                                <>
                                  <FaEyeSlash size={16} />
                                  <span>Hide Editor</span>
                                </>
                              ) : (
                                <>
                                  <FaEye size={16} />
                                  <span>Show Editor</span>
                                </>
                              )}
                            </button>
                          </div>
                          <CommentNameDropdown
                            investigationId={investigationIds}
                            value={commentNamesMap[investigationKey] || null}
                            setFilterValue={(selectedCommentName) =>
                              handleCommentNameChange(
                                department,
                                investigation_name,
                                selectedCommentName,
                              )
                            }
                          />
                        </div>

                        {/* <div className="col-md-8">
                          <h5 style={{ fontSize: "13px" }}>Comments</h5>
                          <div
                            className="form-control"
                            style={{
                              height: "50px",
                              overflow: "auto",
                              resize: "both",
                              whiteSpace: "pre-wrap",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: commentsMap[investigationKey] ?? "",
                            }}
                          />
                        </div> */}

                        {showEditor && (
                          <div className="col-md-8">
                            <TextEditor
                              editorValue={commentsMap[investigationKey] ?? ""}
                              height={200}
                              setEditorValue={(val) =>
                                handleCommentChange(
                                  department,
                                  investigation_name,
                                  val,
                                )
                              }
                              showHeader={false}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                },
              ),
          )}

        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* <div className="form-group mt-2">
              <h5 style={{ fontSize: "13px" }}>Investigation Comments Name</h5>
              <CommentNameDropdown
                investigationId={investigationId}
                setFilterValue={setCommentName}
              />
            </div> */}
            <div className="form-group">
              <h5 style={{ fontSize: "13px" }}>
                Internal Doctor{" "}
                {status === "APPROVED" && (
                  <span style={{ color: "red" }}> *</span>
                )}
              </h5>
              <InternalDoctorDropdown
                employee_id={jwtAuthreducer?.user?.employee_code_id}
                setFilterValue={setFilterValue}
                doctorId={doctorId}
                setdoctorId={setdoctorId}
                isRequired={status === "APPROVED"}
                status={status}
              />
            </div>
            <div className="form-group mt-2" style={{ marginTop: "10px" }}>
              <h5 style={{ fontSize: "13px" }}>Actions</h5>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {reportRoleData?.data?.only_roles_data &&
                  Object.keys(reportRoleData.data.only_roles_data).map(
                    (roleName) => {
                      const isDisabled =
                        roleName === "APPROVED" && !filterValue && !doctorId;

                      return (
                        <button
                          key={roleName}
                          type="button"
                          className="btn btn-primary"
                          disabled={isDisabled}
                          style={{
                            fontSize: "14px",
                            textTransform: "capitalize",
                          }}
                          onClick={() => {
                            if (roleName === "PREVIEW") {
                              const invId =
                                rowData?.invesigation_data?.investigation_id ||
                                rowData?.investigation_data?.investigation_id;
                              const appId = rowData?.appointment_id;

                              const url = `/ResultEntryPdf?display_header=true&display_footer_banner=false&display_banner=false&display_footer=true&display_address=true&display_watermark=true&investigation_ids=${invId}&appointment_id=${appId}`;

                              window.open(url, "_blank");
                            } else {
                              setStatus(roleName);
                              handleSave(roleName);
                            }
                          }}
                        >
                          {roleName}
                        </button>
                      );
                    },
                  )}
              </div>
            </div>
          </div>

          {/* <div className="form-group mt-2" style={{ flex: 1 }}>
            <h5 style={{ fontSize: "13px" }}>Comments</h5>
            <TextEditor editorValue={comment} height={155} setEditorValue={setComment} />
          </div> */}
        </div>
        {/* <button
          type="submit"
          className="btn btn-primary mt-4"
          onClick={handleSave}
          // disabled={!status}
          // disabled={
          //   status === "APPROVED" && (!filterValue || filterValue === "")
          // }
          disabled={status === "APPROVED" && !filterValue && !doctorId}
        >
          Save
        </button> */}
      </div>
    </>
  );
};

export default AddObservationValue;
